/**
 * Simplified Chinese translation strings for BetMeBro
 */
export const zh = {
  // Brand identity
  brand: {
    name: "BetMeBro",
    tagline: "预测. 下注. 赢取.",
    logoLabel: "BetMeBro 标志",
  },

  // Common UI elements
  common: {
    loading: "加载中...",
    close: "关闭",
    copied: "已复制!",
    retry: "重试",
    tryAgain: "重试",
  },

  // Navigation
  nav: {
    markets: "市场",
    worldCup: "世界杯",
    profile: "个人资料",
    leaderboard: "排行榜",
    createMarket: "创建市场",
    admin: "管理后台",
  },

  // 管理后台访问门禁（RBAC，2026-06-12）
  admin: {
    exit: "退出后台",
    access: {
      title: "管理后台",
      connectPrompt: "请连接钱包以访问管理后台。",
      denied: "此钱包没有管理角色。请联系管理员授予 operator 权限。",
    },
  },

  // Authentication and wallet
  auth: {
    connectWallet: "连接钱包",
    walletDisconnected: "钱包已断开",
    walletRequired: "需要连接钱包",
    loggedInAs: "已登录为 {{address}}。点击打开钱包菜单",
    connectWalletToSignIn: "连接Solana钱包以登录",
    logoutFromWallet: "从钱包登出",
    logout: "登出",
    reconnectWallet: "重新连接钱包",
    reconnectWalletDesc: "您的钱包已断开连接。请重新连接以继续。",
    connectAndSignIn: "连接并登录",
  },

  // Wallet connection
  wallet: {
    connecting: "连接中...",
    connectWallet: "连接钱包",
    copyAddress: "复制地址",
    viewOnExplorer: "在浏览器中查看",
    disconnect: "断开连接",
    balance: "余额",
    refresh: "刷新",
    send: "发送",
    transfer: "转账",
    rename: {
      edit: "重命名钱包",
      placeholder: "钱包名称",
      failed: "重命名失败",
    },
    receive: {
      menuItem: "收款",
      title: "收款",
      scanHint: "扫描二维码或复制地址以收款。",
      addressLabel: "你的钱包地址",
      qrAlt: "你钱包地址的二维码",
      copy: "复制地址",
      copied: "地址已复制到剪贴板",
      copyFailed: "复制失败 —— 请手动选中并复制地址。",
      done: "完成",
    },
    selectToken: "选择代币",
    recipientAddress: "收款地址",
    pasteAddress: "粘贴",
    amount: "金额",
    available: "可用",
    maxAmount: "最大",
    invalidAddress: "无效的钱包地址",
    selfTransferError: "不能转账给自己",
    insufficientBalance: "余额不足",
    solReserveHint: "保留少量 SOL 用于交易手续费",
    confirmInWallet: "请在钱包中确认...",
    transferSuccess: "转账成功！",
    transferSubmitted: "已提交 — 链上确认中",
    transferSubmittedDesc:
      "交易已广播。链上确认后会出现在交易历史中（通常一分钟内）。",
    transferSubmittedToast: "交易已提交，稍后会在链上确认。",
    done: "完成",
    tryAgain: "重试",

    modal: {
      secureBadge: "安全",
      protectedFooter: "由 BetMeBro 提供保护",
      titles: {
        select: "连接钱包",
        create: "创建新钱包",
        import: "导入钱包",
        ledger: "连接 Ledger",
        mnemonic: "备份助记词",
        backup: "备份你的钱包",
        default: "钱包",
      },
      descriptions: {
        select: "选择连接方式",
        create: "通过生物识别验证创建新钱包",
        import: "使用助记词或私钥导入现有钱包",
        ledger: "连接你的 Ledger 硬件钱包",
        mnemonic: "请按顺序抄下这些单词并妥善保管",
        backup: "下载已加密的钱包备份文件",
      },
    },

    select: {
      yourWallets: "我的钱包",
      searchPlaceholder: "搜索钱包...",
      backupBadge: "去备份",
      noMatch: '没有匹配 "{{query}}" 的钱包',
      maxLocalReached: "本地钱包已达上限 {{max}} 个。",
      maxLedgerReached: "Ledger 钱包已达上限 {{max}} 个。",
      getStarted: "开始使用",
      hardwareWalletHeading: "硬件钱包",
      walletFallbackName: "钱包 {{prefix}}",
      create: {
        title: "创建新钱包",
        desc: "由 TouchID / FaceID 保护",
      },
      import: {
        title: "导入现有钱包",
        desc: "通过助记词或私钥",
      },
      ledger: {
        title: "连接 Ledger",
        desc: "使用你的硬件钱包",
      },
      prfUnsupported:
        "当前浏览器不支持 WebAuthn PRF。请使用 Chrome 或 Edge，并配合支持的验证器。",
      webHidUnsupported:
        "当前浏览器不支持 WebHID。请使用 Chrome 或 Edge 连接 Ledger。",
    },

    create: {
      back: "返回",
      nameLabel: "钱包名称（可选）",
      namePlaceholder: "我的钱包",
      twoStepTitle: "两步验证",
      twoStepIntro: "为最大化安全，你将完成两次身份验证：",
      twoStepStep1: "创建安全凭证",
      twoStepStep2: "加密钱包密钥",
      biometricTitle: "生物识别验证",
      biometricDesc:
        "你的钱包将由 TouchID、FaceID 或 Windows Hello 保护。每次签名交易都需要重新验证。",
      submit: "使用生物识别创建",
      progress: {
        stepOf: "第 {{current}} / {{total}} 步",
        step1Title: "正在创建安全凭证",
        step1Desc: "使用 Face ID 或 Touch ID 验证身份",
        step2Title: "正在加密钱包",
        step2Desc: "再次验证以完成安全设置",
      },
      mnemonic: {
        warningLabel: "重要提示：",
        warningBody:
          "请按顺序抄下这 24 个单词。一旦失去本设备的访问权限，这是恢复钱包的唯一方式。绝不要分享给任何人。",
        copy: "复制单词",
        copied: "已复制！",
        hint: "下一步会要求你验证备份",
        continue: "继续验证",
      },
      verify: {
        title: "验证你的备份",
        desc: "输入助记词中以下位置的单词，以确认你已正确保存。",
        wordLabel: "第 {{n}} 个单词",
        wordPlaceholder: "请输入第 {{n}} 个单词",
        back: "返回",
        submit: "完成验证",
      },
    },

    mnemonicDisplay: {
      empty: "暂无可用助记词",
      warningTitle: "请抄下这些单词",
      warningDesc:
        "这是你的钱包恢复短语。请把它抄在纸上，妥善保管。绝不要分享给任何人。",
      copy: "复制到剪贴板",
      copied: "已复制！",
      confirmLabel: "我已抄写下助记词并妥善保存",
      done: "完成",
      markBackedUpFailed: "无法保存备份确认，稍后可通过备份提示横幅再次确认。",
    },

    backup: {
      warningTitle: "备份你的钱包",
      warningDesc:
        "下载一份加密的钱包备份。恢复时需要在本设备上完成生物识别验证。",
      infoIntro:
        "你的钱包备份由生物识别（TouchID / FaceID）派生的密钥加密。也就是：",
      info1: "备份只能在本设备恢复",
      info2: "解密时需要进行生物识别",
      info3: "若失去本设备的访问权限，请改用助记词恢复",
      download: "下载加密备份",
      downloadFailed: "备份文件下载失败，请重试。",
      skip: "暂时跳过",
      laterReminder: "稍后随时可在钱包设置中下载备份",
      bannerLabel: "钱包尚未备份",
      bannerCta: "去备份",
    },

    import: {
      back: "返回",
      methods: {
        mnemonic: "助记词",
        privateKey: "私钥",
        backup: "备份文件",
      },
      nameLabel: "钱包名称（可选）",
      namePlaceholder: "已导入钱包",
      backupFileLabel: "备份文件",
      backupFileSelect: "点击选择备份文件",
      backupFileChange: "点击更换",
      backupFileWarning: "⚠️ 仅能在创建备份的同一设备上还原",
      mnemonicLabel: "助记词",
      privateKeyLabel: "私钥",
      mnemonicPlaceholder: "请输入你的 12 或 24 个单词助记词...",
      privateKeyPlaceholder: "请输入 base58 编码的私钥...",
      mnemonicHint: "单词之间用空格分隔",
      privateKeyHint: "base58 编码的 64 字节私钥",
      backupBoxTitle: "从备份恢复",
      backupBoxDesc: "备份文件中包含已加密的钱包。恢复后需要进行生物识别验证。",
      secureBoxTitle: "安全导入",
      secureBoxDesc:
        "你的钱包将由生物识别加密保护，原始助记词 / 私钥不会被存储。",
      submitBackup: "从备份恢复",
      submitImport: "使用生物识别导入",
      restoring: "恢复中...",
      importing: "导入中...",
      errors: {
        selectBackupFile: "请选择备份文件",
        enterValue: "请输入助记词或私钥",
      },
    },

    ledger: {
      back: "返回",
      step1Title: "连接 Ledger 设备",
      step1Desc: "插入 Ledger 并输入 PIN 解锁",
      step2Title: "打开 Solana App",
      step2Desc: "在 Ledger 上进入并打开 Solana 应用",
      step3Title: "点击连接",
      step3Desc: "点击下方按钮，并在浏览器弹窗中选择你的 Ledger",
      pathLabel: "派生路径",
      pathDefault: "默认（{{path}}）",
      pathLive: "Ledger Live（{{path}}）",
      pathAccount: "账户 {{n}}（{{path}}）",
      pathHint: "大多数用户保持默认即可",
      submit: "连接 Ledger",
      connectingTitle: "正在连接 Ledger...",
      connectingDesc: "请在浏览器弹窗中选择设备，并在 Ledger 上确认",
      blindWarningTitle: "请启用盲签",
      blindWarningDesc:
        "部分交易需要在 Ledger 的 Solana App 设置中启用盲签后才能签署。",
    },

    compat: {
      title: "钱包兼容性提示",
      desc: "应用正运行在嵌入式上下文中，可能影响钱包连接。出于安全限制，部分钱包（如 Trust Wallet）无法在此模式连接。",
      openNewTab: "在新标签页打开",
      dismiss: "知道了",
      recommended: "推荐使用：Phantom、Solflare 或 Coinbase Wallet",
      inlineTitle: "检测到钱包支持受限",
      inlineDescPrefix: "部分钱包可能无法正常连接。为获得最佳体验，",
      inlineDescLink: "在新标签页打开",
      inlineDescSuffix: "。",
    },

    info: {
      noWallet: "未连接钱包",
      addressCopiedTitle: "地址已复制",
      addressCopiedDesc: "钱包地址已复制到剪贴板",
      copyAddressAria: "复制地址",
      viewOnExplorerAria: "在浏览器中查看",
      solBalance: "SOL 余额",
      tokenHoldings: "代币持仓",
      tokenCount_one: "{{count}} 个代币",
      tokenCount_other: "{{count}} 个代币",
      tokenAmount: "{{amount}} 个代币",
      noTokens: "暂无代币",
      viewTokenAria: "查看代币",
    },

    txConfirm: {
      defaultTitle: "确认交易",
      hint: "确认后，钱包会要求指纹或硬件密钥批准。如需中止，请在此处取消，签名请求不会发出。",
      cancel: "取消",
      confirm: "确认并签名",
      titles: {
        placeBet: "下注",
        // Phase F (2026-05-18, economic-model-v2 G5 merged): "结算"
        // 同时覆盖 winner（领奖）和 loser（关账户回收 rent）两条路径
        settleBet: "结算下注",
        reclaimBet: "领回本金",
        sendSol: "发送 SOL",
        sendToken: "发送代币",
        createMarket: "创建市场",
        closeMarket: "关闭市场",
        drainOracleReq: "回收预言机租金",
      },
      labels: {
        market: "市场",
        amount: "金额",
        wallet: "钱包",
        to: "接收方",
        from: "发送方",
        question: "问题",
        resolvesAt: "结算时间",
        creationFee: "创建费",
        proposerReward: "预言机应答奖励",
        totalCost: "总下注",
        totalPayout: "总潜在收益",
      },
      sides: {
        yes: "是",
        no: "否",
      },
    },

    recipientPlaceholder: "So1ana...",
  },

  // Error messages
  errors: {
    marketNotFound: "未找到市场",
    marketNotFoundDesc: "您查找的市场不存在或已被移除。",
    loadFailed: "加载失败",
    loadFailedDesc: "加载数据时出错。",
    noPositions: "暂无仓位",
    noPositionsDesc: "您还没有仓位。开始下注吧！",
    noProfile: "暂无资料",
    noProfileDesc: "下第一注以创建您的资料！",
    emptyMarkets: "未找到市场",
    emptyMarketsDesc: "目前没有可用的市场。请稍后再来！",
  },

  // Beta banner (testing-phase notice at top of every page)
  betaBanner: {
    label: "测试版",
    body: "BetMeBro 当前运行在 Solana 测试网（Devnet），仅使用测试 SOL 结算，请勿使用真实资金。",
    dismiss: "关闭测试版提示",
  },

  // Breaking news sidebar
  breakingNews: {
    ariaLabel: "最新动态",
    heading: "最新",
    dismiss: "关闭最新动态",
    loading: "加载中…",
    error: "数据源暂不可达",
    empty: "暂无最新赛事",
    live: "进行中",
    ft: "完场",
    canceled: "取消",
    justNow: "刚刚",
    minutesAgo: "{{count}}分钟前",
    hoursAgo: "{{count}}小时前",
    daysAgo: "{{count}}天前",
  },

  // Footer
  footer: {
    privacy: "隐私",
    terms: "使用条款",
    copyright: "BetMeBro © {{year}}",
    // 每页页脚的信任行：声明结算来源（oracle）、链上事实、每个市场单注
    trustLine: "由 detrust-oracle 结算 · 链上 · 每市场限 1 注",
  },

  // Legal pages
  legal: {
    lastUpdated: "最后更新",
    backHome: "返回首页",
    privacy: {
      title: "隐私政策",
      effectiveDate: "2026 年 5 月 12 日",
      intro:
        '本《隐私政策》说明 BetMeBro（以下称"我们"或"本公司"）在您访问 betmebro.fun 提供的网站及预测市场界面（以下称"本网站"）时，如何收集、使用和披露个人信息。使用本网站即表示您同意以下做法。如不同意，请勿使用本网站。',
      sections: {
        introduction: {
          title: "1. 引言",
          body: "本政策适用于本网站及通过本网站提供的各项服务，不适用于您通过本网站链接访问的第三方网站、智能合约、预言机、钱包或服务，即使这些第三方与 BetMeBro 互操作。本政策修订后，您继续使用本网站即视为接受修订后的政策。",
        },
        applicability: {
          title: "2. 适用范围",
          body: '"个人信息"指可识别或可合理识别您本人身份的信息。本政策不适用于已脱敏或汇总后无法合理与您关联的数据，也不适用于已公开存在于 Solana 区块链上的信息（如钱包地址、交易签名、链上持仓历史）。',
        },
        collection: {
          title: "3. 信息收集与使用",
          body: "我们收集您直接提供的信息（如显示名、可选订阅的邮箱）、您浏览时自动产生的信息（如 IP 地址、User-Agent、设备标识、日志、Referer）、由您在本网站上的活动衍生的信息（如浏览页面、使用功能）、金融信息（如您的公开钱包地址及链上历史）、移动设备信息以及基于 IP 推断的大致地理位置。我们使用这些信息以运营、保护并改进本网站。",
        },
        minors: {
          title: "4. 未满 18 周岁的未成年人",
          body: "本网站不面向 18 周岁以下的用户。我们不会有意收集未成年人的个人信息。如发现收集了 18 周岁以下个人的信息，我们将予以删除。监护人如认为其孩子向我们提供了个人信息，请通过下方联系方式与我们联系。",
        },
        cookies: {
          title: "5. Cookie 与其他跟踪技术的使用",
          body: "我们使用 Cookie、本地存储及类似技术来识别回访用户、记忆偏好、维持登录会话以及分析使用情况。多数浏览器允许您禁用 Cookie 或清除本地存储，此举可能导致本网站部分功能（包括保持登录态）受损或无法使用。",
        },
        use: {
          title: "6. 您的信息的使用方式",
          body: "我们使用个人信息以提供和维护您的账户、对钱包会话进行身份验证、结算并展示市场、防范欺诈与滥用、就本网站进行沟通、遵守适用法律及合法请求、执行本《条款》以及改进产品质量与性能。",
        },
        sharing: {
          title: "7. 我们如何与其他方共享个人信息",
          body: "我们会与代表我们行事的服务商共享个人信息（如托管、RPC、分析、客服、欺诈检测）、与您授权集成的业务伙伴共享、依据有效法律程序与执法及监管机构共享，并在合并、收购或资产出售情形下与承继方共享。我们不出售个人信息。",
        },
        thirdPartyLinks: {
          title: "8. 第三方链接",
          body: "本网站可能包含指向我们未运营或控制的第三方网站、应用或服务的链接。我们对这些第三方的隐私做法不承担责任，建议您在向其提供个人信息前阅读其隐私政策。",
        },
        analytics: {
          title: "9. 第三方分析与再营销",
          body: "我们可能使用第三方分析服务（如 Web 分析工具）来了解访客如何使用本网站。这些服务可能依据其自身政策设置自有 Cookie 并收集设备与使用信息。您可以通过浏览器设置或行业通用的退出工具退出多数第三方分析服务。",
        },
        preferences: {
          title: "10. 个人通信偏好",
          body: "您可在账户设置中访问、更新或删除与账户关联的链下个人信息，并可随时通过取消订阅链接或与我们联系退订非必要邮件。在您账户处于活跃状态期间，事务性通知及法律通知可能持续发送。",
        },
        transfer: {
          title: "11. 数据传输",
          body: "BetMeBro 在全球运营。使用本网站即表示您同意将个人信息传输至您所在地之外的司法管辖区进行处理与存储，该等司法管辖区的数据保护法律可能与您所在国家不同。",
        },
        rights: {
          title: "12. 您的数据保护权利",
          body: "在适用法律范围内，您可能有权访问我们持有的关于您的个人信息、更正不准确数据、反对或限制特定处理、请求删除以及获得可携带的数据副本。如需行使这些权利，请通过下方邮箱联系我们。我们将在适用法律要求的期限内答复。",
        },
        deletion: {
          title: "13. 个人数据的返还/删除",
          body: "在有效请求下，并在符合法律法规义务的前提下，我们将返还或安全销毁所持有的关于您的链下个人信息。请注意，已记录在 Solana 区块链上的信息无法由我们删除。",
        },
        changes: {
          title: "14. 本政策的变更",
          body: "我们可能随时修订本政策。修订版将在此发布并更新生效日期。在适当情况下，重大变更将在产品内予以提示。您在变更生效后继续使用本网站即视为接受修订后的政策。",
        },
        jurisdiction: {
          title: "15. 管辖",
          body: "本网站自 BetMeBro 注册地运营，本政策受其注册地法律管辖，不适用法律冲突规则。",
        },
        contact: {
          title: "16. 联系我们",
          body: "如对本政策有疑问或希望行使上述任一权利，请联系 privacy@betmebro.fun。",
        },
      },
    },
    terms: {
      title: "使用条款",
      effectiveDate: "2026 年 5 月 12 日",
      intro:
        '本《使用条款》（以下称"本条款"）构成您与 BetMeBro（以下称"本公司"、"我们"或"我们的"）之间具有约束力的协议，规范您访问和使用 BetMeBro 网站及界面（以下称"本网站"）。访问或使用本网站即表示您同意本条款。如不同意，请勿访问或使用本网站。若您位于受限司法管辖区、为其居民，或受制于制裁，则不得使用本网站。',
      sections: {
        introduction: {
          title: "1. 引言",
          body: "本条款适用于您对本网站的使用，包括通过本网站提供的所有功能、工具、内容与应用。本网站面向相关活动合法的司法管辖区的用户。您单独负责判断访问或使用本网站是否符合您所在司法管辖区的法律，并遵守所有适用法律。",
        },
        site: {
          title: "2. 本网站及功能",
          body: "本网站为运行于 Solana 区块链上的非托管预测市场协议提供信息化界面。本公司不运营交易所、不托管用户资金、不代表您执行交易、不控制链上交易。所有交易与结算均通过您使用自有钱包直接与智能合约交互完成。您单独对自己签署并广播的交易承担责任。",
        },
        modifications: {
          title: "3. 修改",
          body: "我们可能随时（在通知或不通知的情况下）修改本条款、本网站或任何功能。如法律要求，我们将就重大变更提供合理通知。变更生效后您继续使用本网站即视为接受修订后的条款。",
        },
        conduct: {
          title: "4. 您的责任、陈述与禁止行为",
          body: "使用本网站即表示您声明：（a）您已达到所在司法管辖区的法定年龄；（b）您不受经济制裁，且不位于受限司法管辖区；（c）您具有签署本条款的法律行为能力；（d）您对本网站的使用符合适用法律。您同意不利用本网站从事市场操纵、虚假交易、自我对赌、洗钱、欺诈或其他非法活动，且不干扰本网站或其智能合约、不试图绕过访问控制、不以违反本条款的方式抓取本网站。",
        },
        ip: {
          title: "5. 知识产权",
          body: "本网站及其设计、文本、图形与非开源代码归本公司或其许可方所有，受知识产权法保护。我们授予您有限的、非排他、不可转让的许可，用于在符合本条款的前提下出于个人非商业目的访问与使用本网站。开源组件按其各自许可证约定。当您向本网站提交内容（例如评论、市场问题、个人资料）时，您即授予本公司在运营本网站之必要范围内的全球、免版税的托管、展示与使用许可。",
        },
        disclaimers: {
          title: "6. 免责与责任限制",
          body: '本网站按"现状"和"可用即提供"的方式提供，不附带任何明示或暗示的担保。我们不保证本网站不间断、无错误或安全，不保证预言机数据准确或及时，亦不保证智能合约无漏洞。在适用法律允许的最大范围内，本公司因本网站产生或与之相关的累计责任不超过 100 美元；本公司不对任何间接、附带、特殊、后果性、惩罚性或示范性损害，或任何利润、数据或数字资产损失承担责任。',
        },
        law: {
          title: "7. 适用法律及争议解决",
          body: "本条款受本公司注册地法律管辖，不适用法律冲突规则。在启动任何正式程序前，您同意先以诚信方式联系我们，并尝试以非正式方式解决争议至少三十（30）日。",
        },
        arbitration: {
          title: "8. 仲裁协议及集体诉讼弃权",
          body: "无法以非正式方式解决的争议，应专属地通过依据本公司所在地认可的商事仲裁规则进行的终局且具有约束力的个人仲裁解决。您与本公司各自放弃陪审团审判权及参与集体诉讼、集体仲裁或代表性程序的权利。如适用法律禁止对某项请求进行仲裁，则该项请求单独不适用本条。",
        },
        general: {
          title: "9. 一般条款",
          body: "本条款连同《隐私政策》构成您与本公司就本网站达成的完整协议。您不得转让本条款；我们可在合并、收购或资产出售情形下将其转让给承继方。未行使任何条款不构成弃权。如任一条款被认定不可执行，其余条款仍具完全效力。如有疑问或通知，请发送至 legal@betmebro.fun。",
        },
      },
    },
  },

  // Login modal
  loginModal: {
    reviewSignatureRequest: "审核签名请求",
    reviewMessageBeforeSigning: "签名前请审核以下消息",
    signingMessage: "签名消息",
    completeSignatureRequest: "请在钱包中完成签名请求",
    success: "成功！",
    youAreSignedIn: "您现已登录",
    error: "错误",
    somethingWentWrong: "出现问题",
    waitingForSignature: "等待签名",
    approveInWallet: "请在钱包中批准签名请求...",
    preparingSignature: "正在准备签名请求...",
    signingIn: "登录中",
    preparingAuth: "正在准备认证...",
    authenticationSuccessful: "认证成功！",
    authenticationFailed: "认证失败",
    errorOccurred: "认证过程中发生错误，请重试。",
    cancel: "取消",
    signMessage: "签名消息",
    checkWalletForSignature: "请检查钱包以获取签名请求...",
    close: "关闭",
    tryAgain: "重试",
  },

  // World Cup 2026 event page
  worldCup: {
    title: "2026 世界杯",
    subtitle: "FIFA 2026 世界杯 · 6月11日 – 7月19日 · 48 队 104 场",
    tabs: { schedule: "赛程", games: "比赛", markets: "市场" },
    fixturesError: "赛程加载失败。",
    marketsError: "世界杯市场加载失败。",
    retry: "重试",
    group: "{{id}} 组",
    knockout: {
      r32: "32 强",
      r16: "16 强",
      qf: "1/4 决赛",
      sf: "半决赛",
      third: "季军赛",
      final: "决赛",
    },
    tbd: "待定",
    live: "直播中",
    ft: "完场",
    postponed: "延期",
    canceled: "取消",
    outcome: { hw: "{{team}} 胜", dr: "平局", aw: "{{team}} 胜" },
    createSlot: "创建",
    matchStarted: "已开赛",
    noGamesYet: "暂无已确定对手的比赛。",
    noMarketsYet: "还没有世界杯市场——去「比赛」页创建第一个吧。",
    marketsCount: "{{count}} 个市场",
    matchCount: "{{count}} 场比赛",
  },

  // Markets
  markets: {
    // 首页 hero — 英文 "Think you know? Bet me." 是 BetMeBro 品牌双关；
    // 中文落地为"预测—印证"叙事，避开"赌"字以契合预测市场定位与合规边界
    heroEyebrow: "在线预测市场",
    heroTitlePrefix: "你的判断？",
    heroTitleAccent: "来印证。",
    featured: "精选市场",
    endingSoon: "即将结束",
    bettors: "参与者",
    participants: "参与者",
    coveragePool: "保障资金池",
    placeBet: "下注",
    placeABet: "去下注",
    insureBadge: "保险",
    hot: "火热",
    allMarkets: "全部市场",
    pool: "奖池",
    // Detail page — odds 走势图卡
    oddsHistory: "赔率走势",
    ticks: "笔",
    // Detail page — 链上字段拼出的规则区块标签
    resolution: "结算规则",
    // Home page — 替代跑马灯
    recentActivity: "最近活动",
    lastNBets: "最近 {{count}} 笔",
    closesIn: "剩余",
    // 紧凑标签，给首页 PlatformStats 横排 <dl> 用，单词不换行
    platformStats: {
      markets: "市场",
      bettors: "参与者",
      volume: "交易量",
      liveNow: "实时",
    },
    create: {
      eyebrow: "新建市场",
      title: "创建市场",
      walletRequiredDesc: "请先连接钱包以创建市场。",
      questionLabel: "问题",
      questionHintInsure:
        "保险市场通常描述有下行风险的事件——航班延误、天气灾害、协议漏洞、资产暴跌。问题仍然以 YES / NO 形式结算。",
      modeTabAria: "市场模式",
      modeTab: {
        bet: "投注",
        insure: "保险",
      },
      modeTooltip: {
        bet: "二元 YES/NO 结果。投注者押注事件是否发生。",
        insure: "买家支付保费换取保障；卖家抵押资金提供保障。",
      },
      byMode: {
        bet: {
          subtitle:
            "任何人都可以创建市场。你需要支付少量创建费用 + 约 0.003 SOL 的 Solana 租金。市场由管理员裁决（v1）—— 生命周期：开放 → 事件 → 结算 → 用户领取奖金。",
          questionPlaceholder:
            "比如：BTC 会在 2026-06-15 之前突破 12 万美元吗？",
          submitLabel: "创建市场",
          closeLabel: "投注截止",
          closeHint:
            "留空使用默认值（= 事件开始时间）。可手动提前关闭投注，但不能晚于事件开始。",
        },
        insure: {
          subtitle:
            "任何人都可以创建保险市场。买家支付保费，卖家抵押担保。由管理员裁决（v1）—— 保险生命周期：开放 → 事件 → 结算 → 触发时买家领取赔付。",
          questionPlaceholder: "比如：2026-06-15 SFO 航班延误超过 2 小时吗？",
          submitLabel: "创建保险",
          closeLabel: "投保截止",
          closeHint:
            "留空使用默认值（= 事件开始时间）。可手动提前停止投保，但不能晚于事件开始。",
        },
      },
      eventStart: "事件开始",
      eventEnd: "事件结束",
      timezoneHint:
        "下方时间按您的本地时区解释（{{tz}} · {{offset}}），提交前请再核对一次。",
      resolutionDeadline: "结算截止时间",
      resolutionDeadlineHint:
        "留空使用默认值（= 事件结束后 24 小时）。可手动延后，但不能超过事件结束后 7 天（合约限制）。",
      advancedTiming: "高级时间设置",
      advancedTimingHint: "可选 — 默认从事件窗口推算",
      fees: {
        title: "你需要支付的费用",
        creation: "创建费用：",
        rent: "Solana 租金（市场 + 托管）：",
        oracleRent: "DataRequest 租金（预言机）：",
      },
      submit: "创建市场",
      submitting: "创建中…",
      wcMode: {
        eyebrow: "世界杯市场",
        lockedHint: "问题与时间由所选比赛派生，不可编辑。",
        matchLabel: "比赛",
        outcomeLabel: "结果",
        kickoffLabel: "开球时间（本地时区）",
        invalidParams: "比赛链接无效或已开赛——你仍可在下方自由创建市场。",
      },
      errors: {
        questionMin: "问题至少需要 8 个字符",
        questionMax: "问题不能超过 200 个字符",
        noMarkerPrefix: "问题不能以「[」开头——方括号前缀保留给赛事市场。",
        required: "必填",
        mustBeFuture: "必须为将来时间",
        mustBeFutureBuffer: "必须至少在 5 分钟之后",
        afterEventStart: "必须晚于事件开始时间",
        atOrBeforeEventStart: "必须不晚于事件开始时间",
        afterEventEnd: "必须晚于事件结束时间",
        within7Days: "不能超过事件结束后 7 天",
        walletNotConnected: "钱包未连接。",
      },
      toasts: {
        success: "市场已创建！",
        pending: "市场已提交 — 链上正在确认，稍后将出现在列表中。",
        failure: "无法创建市场",
      },
    },
  },

  // Market detail page — result-specific resolution strings (mirrors en).
  marketDetail: {
    resolution: {
      outcomeLabel: "结算结果",
      resolvedAtLabel: "结算时间",
      yesWon: "YES 胜",
      noWon: "NO 胜",
      undisputed: "未被挑战",
      challenged: "经历 {{rounds}} 轮挑战",
      voided: "已作废 · 全额退款",
      archived: "DataRequest 已归档",
    },
    settleReady: {
      waiting: "赛事已结束 — 等待预言机敲定",
      refresh: "刷新预言机状态",
      resolvedYes: "预言机已敲定：YES 胜",
      resolvedNo: "预言机已敲定：NO 胜",
      settleCta: "结算下注",
      settleHint: "结算的同时会把裁决结果写上链，其他人的领奖入口也随之解锁。",
      waitingHolder:
        "预言机已敲定 — 任一持仓人完成结算后，市场状态将自动更新。",
    },
  },

  // Betting
  betting: {
    yes: "是",
    no: "否",
    amount: "下注金额",
    placeYourBet: "下注",
    notOnChainYet:
      "该市场仍在同步上链，等链上 create_market 交易确认后即可下注。",
    alreadyBet: "您已在此市场下过一注，每个市场仅允许下注一次。",
    alreadyBetCta: "已下注",
    closesIn: "截止时间",
    insure: {
      buyTab: "\ud83d\udee1 \u6211\u8981\u6295\u4fdd",
      sellTab: "\ud83d\udcb0 \u6211\u8981\u627f\u4fdd",
      premiumAmount: "\u4fdd\u8d39\u91d1\u989d (\u4f60\u652f\u4ed8)",
      premium: "\u4fdd\u8d39 (\u4f60\u652f\u4ed8)",
      estimatedPayout: "\u9884\u4f30\u8d54\u4ed8 (\u51fa\u9669\u65f6)",
      payoutRatio: "\u8d54\u4ed8\u500d\u7387",
      triggerCondition: "\u89e6\u53d1\u6761\u4ef6",
      verificationSource: "\u9a8c\u8bc1\u6765\u6e90",
      howItWorks: "\u8fd0\u4f5c\u65b9\u5f0f\uff1a",
      howItWorksDesc:
        "\u652f\u4ed8\u4fdd\u8d39 \u2192 \u89e6\u53d1\u6761\u4ef6\u6ee1\u8db3 \u2192 Oracle \u9a8c\u8bc1 \u2192 \u81ea\u52a8\u8d54\u4ed8\u5230\u94b1\u5305\uff08\u65e0\u9700\u7533\u8bf7\uff09",
      confirmBuy: "\u786e\u8ba4\u6295\u4fdd",
      payPrefix: "\u652f\u4ed8",
      underwriteAmount:
        "\u627f\u4fdd\u91d1\u989d (\u9501\u5b9a\u81f3\u7ed3\u7b97)",
      lockedFunds: "\u9501\u5b9a\u8d44\u91d1",
      expectedIncome: "\u9884\u671f\u4fdd\u8d39\u6536\u5165",
      lockPeriod: "\u9501\u5b9a\u671f",
      expectedYield: "\u9884\u671f\u6536\u76ca",
      expectedYieldProb: "\u6982\u7387",
      maxRisk: "\u6700\u5927\u98ce\u9669",
      maxRiskProb: "\u6982\u7387",
      confirmSell: "\u786e\u8ba4\u627f\u4fdd",
      lockPrefix: "\u9501\u5b9a",
      riskWarningTitle: "\u26a0 \u98ce\u9669\u63d0\u793a",
      riskWarningDesc:
        "\u8d44\u91d1\u5c06\u9501\u5b9a\u81f3\u4fdd\u969c\u671f\u7ed3\u675f\u3002\u5982\u679c\u89e6\u53d1\u6761\u4ef6\u6ee1\u8db3\uff0c\u4f60\u5c06\u635f\u5931\u5168\u90e8\u627f\u4fdd\u8d44\u91d1\u3002",
    },

    buy: "\u4e70\u5165",
    sell: "\u5356\u51fa",

    confirmModal: {
      title: "\u786e\u8ba4\u4e0b\u6ce8",
      marketLabel: "\u5e02\u573a",
      positionLabel: "\u9009\u8fb9",
      sideAtOdds: "{{label}} @ {{odds}}%",
      amountLabel: "\u91d1\u989d",
      potentialPayoutLabel: "\u6f5c\u5728\u6536\u76ca",
      payoutWithReturn: "{{payout}} SOL (+{{returnPct}}%)",
      submitButton: "\u7b7e\u540d\u5e76\u4e0b\u6ce8",
      cancelButton: "\u53d6\u6d88",
    },

    payoutCalc: {
      youPay: "\u652f\u4ed8",
      potentialPayout: "\u6f5c\u5728\u6536\u76ca",
    },

    insureSelector: {
      buyTitle: "\u6295\u4fdd",
      buySubtitle: "\u83b7\u5f97\u4fdd\u969c",
      sellTitle: "\u627f\u4fdd",
      sellSubtitle: "\u8d5a\u53d6\u4fdd\u8d39",
    },

    outcomeSelector: {
      bet: {
        yes: "\u662f",
        yesSub: "\u6211\u770b\u4f1a",
        no: "\u5426",
        noSub: "\u6211\u770b\u4e0d\u4f1a",
      },
      insure: {
        yes: "\u6295\u4fdd",
        yesSub: "\u83b7\u5f97\u4fdd\u969c",
        no: "\u627f\u4fdd",
        noSub: "\u63d0\u4f9b\u4fdd\u969c",
      },
    },

    amountInput: {
      placeholder: "\u81ea\u5b9a\u4e49\u91d1\u989d",
      max: "\u6700\u5927\uff1a{{amount}} SOL",
    },

    social: {
      peopleBetting: "\u4eba\u53c2\u4e0e\u6295\u6ce8",
      participants: "\u4f4d\u53c2\u4e0e\u8005",
    },

    oracle: {
      settlementLabel: "\u7ed3\u7b97\u65b9\uff1aDeTrust Oracle",
      settlementSubtitle:
        "\u94fe\u4e0a\u9a8c\u8bc1 \u00b7 \u591a\u65b9\u5171\u8bc6",
      verify: "\u94fe\u4e0a\u9a8c\u8bc1",
    },

    riskDisclaimer: {
      title: "\u98ce\u9669\u63d0\u793a",
      body: "\u9884\u6d4b\u5e02\u573a\u5177\u6709\u6295\u673a\u6027\u3002\u4f60\u53ef\u80fd\u635f\u5931\u5168\u90e8\u6295\u6ce8\u91d1\u989d\u3002\u8bf7\u53ea\u6295\u5165\u4f60\u80fd\u627f\u53d7\u635f\u5931\u7684\u90e8\u5206\u3002\u8fc7\u5f80\u8868\u73b0\u4e0d\u4ee3\u8868\u672a\u6765\u7ed3\u679c\u3002BetMeBro \u4e0d\u63d0\u4f9b\u6295\u8d44\u5efa\u8bae\u3002",
    },

    insurePayout: {
      buyerLabel: "\u4f60\u7684\u4fdd\u969c",
      buyerSubtitle: "\u4e8b\u4ef6\u53d1\u751f\u65f6\u7684\u8d54\u4ed8",
      sellerLabel: "\u5df2\u8d5a\u4fdd\u8d39",
      sellerSubtitle: "\u4f60\u7684\u524d\u7f6e\u6536\u76ca",
      premiumRate: "\u4fdd\u8d39\u7387\uff1a{{value}}%",
      buyerCost: "\u6210\u672c\uff1a{{amount}} SOL",
      sellerLiability: "\u6700\u5927\u98ce\u9669\uff1a{{amount}} SOL",
    },
  },

  // Portfolio
  portfolio: {
    activeBets: "进行中",
    resolvedBets: "已结算",
    claimable: "可领取",
    noBets: "暂无投注",
    startBetting: "开始投注以查看您的仓位",
    claim: "领取",
    refundable: "可退款",
    refunded: "已退款",
    reclaim: "领回",
    recoverRent: "回收租金",
    expirable: "可领回",
    profit: "收益",
    wagered: "投注额",
    payout: "支付",
    potentialPayout: "潜在收益",
    soClose: "差一点！",
    won: "赢",
    lost: "输",
    active: "进行中",
    showingResults_one: "{{count}} 个仓位",
    showingResults_other: "{{count}} 个仓位",
    yourPositions: "您的仓位",
    unrealizedPnL: "未实现盈亏",
    realizedPnL: "已实现盈亏",
    pageTitle: "我的仓位",
    eyebrow: "你的下注",
    walletRequired: "请连接钱包以查看你的仓位。",
    loadError: "加载仓位失败",
    unexpectedError: "出现未知错误。",
    retry: "重试",
    emptyTitle: "暂无仓位",
    emptyDesc: "浏览市场并下第一注，开始建立你的仓位。",
    browseMarkets: "浏览市场",
    toasts: {
      reclaimSubmitted: "已提交，余额稍后更新",
      marketResolvedGoSettle: "此市场已裁决，请前往结算/领取",
    },
  },

  // Profile
  profile: {
    title: "我的资料",
    eyebrow: "你的战绩",
    signInPrompt: "请登录以查看您的资料。",
    stats: {
      totalBets: "总投注",
      winRate: "胜率",
      totalProfit: "总收益",
      bestWin: "最佳胜利",
    },
  },

  leaderboard: {
    title: "排行榜",
    rank: "排名",
    player: "玩家",
    winRate: "胜率",
    profit: "收益",
    totalBets: "投注数",
    noEntries: "暂无排行榜数据。",
    topTraders: "顶级交易者",
    anonymous: "匿名",
  },
  errorStates: {
    somethingWentWrong: "出了点问题",
    tryAgainLater: "请稍后重试。",
    emptyHere: "这里还没有内容",
    checkBackLater: "稍后回来查看新内容。",
    transactionFailed: "交易失败",
    transactionFailedDesc: "您的交易无法处理。请确认余额后重试。",
    viewOnExplorer: "在浏览器中查看",
  },
  share: {
    share: "分享",
    shareMarket: "分享此市场",
    twitter: "分享到 X",
    telegram: "分享到 Telegram",
    copyLink: "复制链接",
    more: "更多选项",
    copied: "链接已复制到剪贴板！",
    copyFailed: "复制链接失败",
    shareFailed: "分享失败",
  },
  referral: {
    fetchError: "加载邀请数据失败",
  },
  desktopGate: {
    title: "仅限桌面端",
    description:
      "BetMeBro 针对桌面浏览器优化。请使用更大的屏幕访问以获得最佳体验。",
  },
};
